import { useLazyLoadQuery } from 'react-relay';
import { getDailySnapshotInfo } from '../../graphQLOperations/Dashboard';
import {
  DashboardGetDailySnapshotQuery,
  DashboardGetDailySnapshotQuery$data,
} from '../../graphQLOperations/__generated__/DashboardGetDailySnapshotQuery.graphql';
import { MeterGroup } from 'primereact/metergroup';
import { Card } from 'primereact/card';
import { useEffect, useState } from 'react';
import { MeterTemplate } from './MeterTemplate';
import { MeterOption } from '../../models/MeterOption';
import { getDateByUserTimezone } from '../../businessLogicService/getDateByUserTimezone';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { format } from 'date-fns';
import { CustomBranding, mapCustomBranding } from '../../models/User';
import TeacherDashboardInfoIcon, { InfoType } from './TeacherDashboardInfoIcon';
import { EmptyResultsWidgets, FilterOptionValues } from './TeacherDashboard';

const order = ['N/A', '0%', '20%', '40%', '60%', '80%', '100%'];
const classNames: { [key: string | number]: string } = {
  'N/A': 'not-started',
  0: 'c-keep-learning',
  2: 'c-keep-learning',
  4: 'c-keep-learning',
  6: 'c-almost-there',
  8: 'c-passed',
  10: 'c-passed',
};
export interface DailySnapshotQueryFilters {
  targetGroupIds: string[] | null;
  classIds: string[] | null;
  siteIds: string[] | null;
  gradeLevelIds: string[] | null;
  reportDate: Date;
}

type Props = {
  queryFilters: DailySnapshotQueryFilters;
  filterOption: FilterOptionValues;
  setEmptyResult: React.Dispatch<React.SetStateAction<EmptyResultsWidgets>>;
};

export const DailySnapshot = ({ queryFilters, filterOption, setEmptyResult }: Props) => {
  const [meterValues, setMeterValues] = useState<MeterOption[]>([]);
  const userRedux = useAppSelector(selectUser);
  const miamiBrand =
    userRedux?.network?.customBranding && mapCustomBranding[userRedux.network.customBranding] === CustomBranding.MIAMI;
  const data = useLazyLoadQuery<DashboardGetDailySnapshotQuery>(
    getDailySnapshotInfo,
    {
      filter: queryFilters,
    },
    { fetchPolicy: 'network-only' },
  );

  const meterGroupData = (data: DashboardGetDailySnapshotQuery$data) => {
    const totalStudents = data.studentsSkillAssessmentsDailySnapshot.reduce((acc, el) => acc + el.numberOfStudents, 0);
    const values: MeterOption[] = data.studentsSkillAssessmentsDailySnapshot
      .filter(i => i.numberOfStudents > 0)
      .map(item => {
        const width = (item.numberOfStudents * 100) / Math.floor(totalStudents);
        const label = item.score !== null ? (item.score === 0 ? `${item.score}%` : `${item.score}0%`) : 'N/A';
        const element = {
          label,
          value: width,
          width,
          className: classNames[item.score !== null && item.score !== undefined ? item.score : label],
          score: item.score,
          students: item.numberOfStudents,
          values: [],
          percentages: [0],
          totaLPercent: 0,
        };
        return element;
      });
    values.sort((a, b) => order.indexOf(a.label) - order.indexOf(b.label));
    setMeterValues(values);
  };

  useEffect(() => {
    if (data.studentsSkillAssessmentsDailySnapshot.every(item => item.score === null)) {
      setEmptyResult(prev => ({ ...prev, dailySnapshot: true }));
    } else {
      setEmptyResult(prev => ({ ...prev, dailySnapshot: false }));
      meterGroupData(data);
    }
  }, [data.studentsSkillAssessmentsDailySnapshot]);

  return (
    <Card
      header={
        <div className={`tw-w-full tw-flex tw-justify-between ${miamiBrand ? 'tw-items-center' : 'tw-items-start'}`}>
          <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
            <h2 className=" tw-text-lg md:text-2xl tw-mr-2 tw-not-italic">
              {miamiBrand ? 'Foundations/RTI Monitoring' : 'Daily Snapshot'}
            </h2>{' '}
            <p className="tw-text-neutral-600 tw-font-atkinson tw-text-base tw-m-0">
              {format(getDateByUserTimezone(userRedux?.activeTimeZone || '').zonedDate, 'P')}
            </p>
          </div>
          {miamiBrand && <TeacherDashboardInfoIcon infoType={InfoType.DAILY} />}
        </div>
      }
      className="tw-w-full tw-p-4"
    >
      <div className="tw-w-full">
        <h3 className={`${miamiBrand && 'tw-pb-3'} tw-text-left tw-text-base tw-w-full`}>{`Skill Challenge${
          miamiBrand ? 's' : ' Results'
        }`}</h3>
        <div className="tw-w-full" id="DailySnapshotBar">
          <MeterGroup
            className="nc_metergroup tw-w-full nc_metergroup-wide tw-mb-3 overflow-hidden"
            values={meterValues}
            meter={met => (
              <MeterTemplate filterOption={filterOption} key={(met as MeterOption).label} value={met as MeterOption} />
            )}
          />
        </div>
      </div>
    </Card>
  );
};
